<template>
  <div ref="dropdownRef" class="relative">
    <!-- Trigger Button -->
    <button
      type="button"
      class="w-full group"
      :class="disabled && 'cursor-not-allowed opacity-60'"
      :disabled="disabled"
      :aria-disabled="disabled"
      @click="toggleOpen"
    >
      <div
        class="w-full rounded-xl border bg-white px-3 py-3 text-left shadow-sm transition-all duration-200"
        :class="[
          isOpen
            ? 'border-[#1980e6] ring-2 ring-[#1980e6]/20'
            : disabled
              ? 'border-gray-300 bg-gray-100'
              : 'border-gray-300 hover:border-gray-400',
        ]"
      >
        <div class="flex items-center gap-3">
          <!-- Chain Icon -->
          <div class="relative flex-shrink-0 w-8 h-8">
            <ChainIcon v-if="selectedChain" :chain="selectedChain.id" class="w-full h-full" />
            <div
              v-else
              class="w-full h-full rounded-full bg-gray-100 flex items-center justify-center text-gray-400"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
          </div>

          <!-- Chain Info -->
          <div class="flex-1 min-w-0">
            <div v-if="selectedChain" class="flex items-baseline gap-2">
              <span class="font-semibold text-[#0e141b] text-base">{{ selectedChain.label }}</span>
            </div>
            <div v-else class="text-gray-400">Select chain</div>
          </div>

          <!-- Chevron -->
          <svg
            class="w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0"
            :class="isOpen && 'rotate-180'"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    </button>

    <!-- Dropdown -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div
        v-if="isOpen"
        class="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden"
      >
        <div class="p-2">
          <button
            v-for="chain in chains"
            :key="chain.id"
            type="button"
            class="w-full px-3 py-3 flex items-center gap-3 rounded-xl hover:bg-gray-50 transition-colors"
            :class="isSelected(chain) && 'bg-blue-50'"
            @click="selectChain(chain)"
          >
            <div class="w-8 h-8 flex-shrink-0">
              <ChainIcon :chain="chain.id" class="w-full h-full" />
            </div>
            <div class="flex-1 min-w-0 text-left">
              <span class="font-semibold text-[#0e141b]">{{ chain.label }}</span>
            </div>
            <div v-if="isSelected(chain)" class="text-[#1980e6]">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import ChainIcon from './ChainIcon.vue'
import type { SourceChainOption } from './contracts'

interface Props {
  chains: readonly SourceChainOption[]
  selectedChainId: number
  disabled?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  select: [chain: SourceChainOption]
}>()

const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
const toggleOpen = () => {
  if (!props.disabled) isOpen.value = !isOpen.value
}

const selectedChain = computed(() => {
  return props.chains.find((c) => c.chainId === props.selectedChainId)
})

const isSelected = (chain: SourceChainOption) => {
  return chain.chainId === props.selectedChainId
}

const selectChain = (chain: SourceChainOption) => {
  if (props.disabled) return
  emit('select', chain)
  isOpen.value = false
}

// Close on click outside
const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

watch(isOpen, (open) => {
  if (open) {
    document.addEventListener('click', handleClickOutside)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) isOpen.value = false
  }
)

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
